import { useEffect, useLayoutEffect, useRef, type RefObject } from "react";
import type { MachineArt } from "./art/machine-art";
import {
  createCssMachineMotion,
  type CssMachineMotion,
} from "./css-machine-motion.mjs";

export const useMachineRuntime = (
  stage: RefObject<HTMLElement | SVGSVGElement | null>,
  art: MachineArt,
  active?: boolean | "starting",
) => {
  const motion = useRef<CssMachineMotion | null>(null);
  useLayoutEffect(() => {
    const element = stage.current;
    if (!element) return;
    const controller = createCssMachineMotion(element);
    motion.current = controller;
    return () => {
      controller.destroy();
      motion.current = null;
    };
  }, [art, stage]);
  useLayoutEffect(() => {
    const element = stage.current;
    if (!element) return;
    const next = active ? String(active) : null;
    if (element.getAttribute("data-active") === next) return;
    motion.current?.change(() => {
      if (next) element.setAttribute("data-active", next);
      else element.removeAttribute("data-active");
    });
  }, [active, art, stage]);
  useEffect(() => {
    const element = stage.current;
    const runtime = art.runtime;
    if (!element || typeof IntersectionObserver === "undefined") return;
    let visible = false;
    let loading = false;
    let disposed = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let unmount: (() => void) | undefined;
    const host = element.closest<HTMLElement>("[data-machine-host]");
    const hover = matchMedia("(hover: hover)");
    const load = () => {
      const working =
        element.hasAttribute("data-active") ||
        element.hasAttribute("data-preview");
      if (!runtime || !visible || document.hidden || !working || loading)
        return;
      loading = true;
      const mountRuntime = async () => {
        switch (runtime.kind) {
          case "mechanical-gesture": {
            const { mount } = await import("./runtime/mechanical-gesture.mjs");
            if (!disposed) unmount = mount(element, runtime);
            return;
          }
          case "sump-water": {
            const { mount } = await import("./runtime/sump-water.mjs");
            if (!disposed) unmount = mount(element, runtime);
            return;
          }
          case "tap-drip": {
            const { mount } = await import("./runtime/tap-drip.mjs");
            if (!disposed) unmount = mount(element, runtime);
            return;
          }
          case "intercom-pickup": {
            const { mount } = await import("./runtime/intercom-pickup.mjs");
            if (!disposed) unmount = mount(element, runtime);
            return;
          }
          case "thermostat-dial": {
            const { mount } = await import("./runtime/thermostat-dial.mjs");
            if (!disposed) unmount = mount(element, runtime);
            return;
          }
          case "furnace-fan": {
            const { mount } = await import("./runtime/furnace-fan.mjs");
            if (!disposed) unmount = mount(element);
            return;
          }
          case "breaker-reset": {
            const { mount } = await import("./runtime/breaker-reset.mjs");
            if (!disposed) unmount = mount(element, runtime);
            return;
          }
          default: {
            const unreachable: never = runtime;
            return unreachable;
          }
        }
      };
      void mountRuntime().catch(() => {
        loading = false;
      });
    };
    const stop = () => {
      clearTimeout(timer);
      motion.current?.change(() => element.removeAttribute("data-preview"));
    };
    const test = () => {
      clearTimeout(timer);
      motion.current?.change(() => element.setAttribute("data-preview", ""));
      timer = setTimeout(stop, 1800);
    };
    const trigger = (event: Event) =>
      event.target instanceof Element &&
      !!event.target.closest("[data-machine-trigger]");
    const enter = () => {
      if (hover.matches) test();
    };
    const over = (event: PointerEvent) => {
      if (!hover.matches || !trigger(event)) return;
      if (
        event.relatedTarget instanceof Element &&
        event.relatedTarget.closest("[data-machine-trigger]")
      )
        return;
      test();
    };
    const out = (event: PointerEvent) => {
      if (!trigger(event)) return;
      if (
        event.relatedTarget instanceof Element &&
        event.relatedTarget.closest("[data-machine-trigger]")
      )
        return;
      stop();
    };
    const focus = (event: FocusEvent) => {
      if (trigger(event)) test();
    };
    const blur = (event: FocusEvent) => {
      if (trigger(event)) stop();
    };
    element.addEventListener("pointerenter", enter);
    element.addEventListener("pointerleave", stop);
    host?.addEventListener("pointerover", over);
    host?.addEventListener("pointerout", out);
    host?.addEventListener("focusin", focus);
    host?.addEventListener("focusout", blur);
    const intersection = new IntersectionObserver((entries) => {
      const entry = entries.at(-1);
      visible = !!entry?.isIntersecting;
      visibility();
    });
    const visibility = () => {
      element.setAttribute("data-visible", String(visible && !document.hidden));
      motion.current?.visibility();
      if (!document.hidden) load();
    };
    document.addEventListener("visibilitychange", visibility);
    visibility();
    const changes = new MutationObserver(load);
    intersection.observe(element);
    changes.observe(element, {
      attributes: true,
      attributeFilter: ["data-active", "data-preview"],
    });
    return () => {
      disposed = true;
      clearTimeout(timer);
      element.removeEventListener("pointerenter", enter);
      element.removeEventListener("pointerleave", stop);
      host?.removeEventListener("pointerover", over);
      host?.removeEventListener("pointerout", out);
      host?.removeEventListener("focusin", focus);
      host?.removeEventListener("focusout", blur);
      document.removeEventListener("visibilitychange", visibility);
      element.removeAttribute("data-visible");
      intersection.disconnect();
      changes.disconnect();
      unmount?.();
    };
  }, [art, stage]);
};
